import React from "react";
import { Link } from "react-router-dom";
import { Linkedin, Twitter, Instagram } from "lucide-react";

/**
 * Footer variants:
 *   "light" — legacy ivory/peach (app pages)
 *   "dark"  — cinematic black (marketing surface)
 */
export const Footer = ({ variant = "light" }) => {
  const year = new Date().getFullYear();
  const dark = variant === "dark";

  const cols = [
    {
      label: "Product",
      links: [
        { to: "/templates", label: "Templates" },
        { to: "/pricing", label: "Pricing" },
        { to: "/cover-letters", label: "Cover Letters" },
        { to: "/signup", label: "Get started" },
      ],
    },
    {
      label: "Company",
      links: [
        { to: "/faq", label: "FAQ" },
        { to: "/", label: "Editorial standards" },
        { to: "/", label: "Recruiter approach" },
        { to: "/", label: "Contact" },
      ],
    },
    {
      label: "Legal",
      links: [
        { to: "/", label: "Privacy" },
        { to: "/", label: "Terms" },
        { to: "/", label: "Refund policy" },
        { to: "/", label: "Cookies" },
      ],
    },
  ];

  const surfaceClass = dark
    ? "relative mt-24 border-t border-warm-ivory/10 bg-matte-black"
    : "relative mt-32 border-t border-ink/15 bg-cream";

  const headlineClass = dark
    ? "font-display text-2xl sm:text-3xl leading-[1.1] tracking-tight max-w-md text-warm-ivory"
    : "font-display text-2xl sm:text-3xl leading-[1.1] tracking-tight max-w-md";

  const bodyText = dark ? "mt-5 text-warm-ivory/55 text-sm leading-relaxed max-w-md" : "mt-5 text-ink/70 text-sm leading-relaxed max-w-md";
  const eyebrowClass = dark ? "text-[10px] tracking-[0.24em] uppercase text-burnished-gold font-mono mb-4" : "label-eyebrow mb-4";
  const linkClass = dark ? "text-sm text-warm-ivory/65 hover:text-burnished-gold transition-colors" : "text-sm text-ink/80 hover:text-oxblood transition-colors";
  const ruleClass = dark ? "hairline-dark mb-10" : "rule-mono mb-10";
  const fineprint = dark ? "text-[10px] tracking-[0.24em] uppercase text-warm-ivory/40 font-mono" : "label-eyebrow";
  const socialClass = dark ? "text-warm-ivory/45 hover:text-burnished-gold transition-colors" : "hover:text-oxblood transition-colors";

  return (
    <footer data-testid="site-footer" className={surfaceClass}>
      <div className="max-w-[1480px] mx-auto px-5 sm:px-8 lg:px-12 pt-20 pb-10">
        <div className="grid lg:grid-cols-[1.2fr_3fr] gap-14 mb-20">
          <div>
            <h3 className={headlineClass}>
              The modern professional<br />
              <span className={dark ? "editorial-accent" : "italic text-[var(--accent)]"}>identity layer.</span>
            </h3>
            <p className={bodyText}>
              Recruiter-ready resumes for ambitious candidates worldwide. AI-enhanced storytelling,
              never AI-fabricated. Trusted across 140+ countries.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-10">
            {cols.map((c) => (
              <div key={c.label}>
                <div className={eyebrowClass}>{c.label}</div>
                <ul className="space-y-2.5">
                  {c.links.map((l) => (
                    <li key={l.label}>
                      <Link to={l.to} className={linkClass}>{l.label}</Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <hr className={ruleClass} />

        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className={fineprint}>
            © {year} PrestiqueResume · Elevate your story
            <span className={dark ? "text-warm-ivory/30 mx-2" : "text-ink/30 mx-2"}>·</span>
            <span>
              In partnership with{" "}
              <a
                href="https://www.jobvibe.co.uk"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-jobvibe-link"
                className={dark ? "text-warm-ivory hover:text-burnished-gold transition-colors underline-offset-4 hover:underline" : "text-ink hover:underline"}
              >
                JobVibe
              </a>
            </span>
          </div>
          <div className={`flex items-center gap-4 ${dark ? "" : "text-ink/70"}`}>
            <a href="#" aria-label="LinkedIn" className={socialClass}><Linkedin className="w-4 h-4" /></a>
            <a href="#" aria-label="Twitter" className={socialClass}><Twitter className="w-4 h-4" /></a>
            <a href="#" aria-label="Instagram" className={socialClass}><Instagram className="w-4 h-4" /></a>
          </div>
        </div>
      </div>
    </footer>
  );
};
